/**
 * Download helper that works both in normal windows and inside iframes.
 * Strategy:
 *   1. Try the File System Access API (showSaveFilePicker) — best UX, works everywhere.
 *   2. Fall back to object URL + <a> click on window.top if accessible.
 *   3. Last resort: same-frame <a> click.
 */

const MIME_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  zip: 'application/zip'
};

export const triggerDownload = async (href: string | null, filename: string, blob?: Blob): Promise<void> => {
  // 1. File System Access API (Chrome 86+, Edge 86+)
  if (blob && window.showSaveFilePicker) {
    try {
      const ext = filename.split('.').pop()?.toLowerCase() ?? 'bin';
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: 'File', accept: { [MIME_BY_EXTENSION[ext] ?? 'application/octet-stream']: [`.${ext}`] } }]
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return;
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return; // user cancelled
      // fall through to next strategy
    }
  }

  // 2. Try window.top (works when iframe is same-origin)
  const targetWindow: Window = (() => {
    try { return window.top !== window ? window.top : window; } catch { return window; }
  })() ?? window;

  if (!href && !blob) {
    throw new Error('Download requires either an href or a blob.');
  }

  const url = href ?? URL.createObjectURL(blob as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  try {
    targetWindow.document.body.appendChild(link);
    link.click();
    targetWindow.document.body.removeChild(link);
  } catch {
    // 3. Cross-origin iframe fallback — append to current frame
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  if (!href) setTimeout(() => URL.revokeObjectURL(url), 10000);
};
